import humanize from 'humanize-duration';
import dayjs, { ConfigType } from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import RelativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import { HUMANIZE_DURATION_LANG_MAP } from '@/common/constants/i18n.constants';
import { ALL_VALUES, PRESET_ALL_VALUES } from '@/common/constants/scheduler.constants';
import i18n from '@/common/i18n';
import { uniq } from 'lodash-es';
import { convertToIso } from '@/common/utils/date';

dayjs.extend(minMax);
dayjs.extend(LocalizedFormat);
dayjs.extend(RelativeTime);
dayjs.extend(utc);

function getSlavicForm(c: number): number {
  if (Math.floor(c) !== c) {
    return 2;
  } else if ((c % 100 >= 5 && c % 100 <= 20) || (c % 10 >= 5 && c % 10 <= 9) || c % 10 === 0) {
    return 0;
  } else if (c % 10 === 1) {
    return 1;
  } else if (c > 1) {
    return 2;
  }
  return 0;
}

const afterRu = {
  y(c) {
    return ['лет', 'год', 'года'][getSlavicForm(c)];
  },
  mo(c) {
    return ['месяцев', 'месяц', 'месяца'][getSlavicForm(c)];
  },
  w(c) {
    return ['недель', 'неделю', 'недели'][getSlavicForm(c)];
  },
  d(c) {
    return ['дней', 'день', 'дня'][getSlavicForm(c)];
  },
  h(c) {
    return ['часов', 'час', 'часа'][getSlavicForm(c)];
  },
  m(c) {
    return ['минут', 'минуту', 'минуты'][getSlavicForm(c)];
  },
  s(c) {
    return ['секунд', 'секунду', 'секунды'][getSlavicForm(c)];
  },
  ms(c) {
    return ['миллисекунд', 'миллисекунду', 'миллисекунды'][getSlavicForm(c)];
  },
  decimal: ','
};

export function humanizeMs(ms: number): string {
  return humanize(ms, { language: HUMANIZE_DURATION_LANG_MAP[i18n.locale] || i18n.locale });
}

export function afterTimeHumanize(ms: number): string {
  const humanizer = humanize.humanizer({
    language: HUMANIZE_DURATION_LANG_MAP[i18n.locale] || i18n.locale,
    languages: { ru: afterRu }
  });
  return i18n.t('After {0}', [humanizer(ms)]);
}

export function dateFormatter(dateStr: ConfigType | undefined, format = 'LLL'): string {
  return dateStr ? dayjs(convertToIso(dateStr)).format(format) : i18n.t('figure dash');
}

export function alertDate(dateStr: string): string {
  return dateFormatter(dateStr);
}

export function notificationDate(dateStr: string | Date): string {
  const now = dayjs();
  const inputDate = dayjs(convertToIso(dateStr));

  if (now.isSame(inputDate, 'day')) {
    return inputDate.format('LT');
  }
  return inputDate.format('L');
}

export function serversDate(dateStr: string): string {
  return dateFormatter(dateStr, 'LLL');
}

export function abgwRegistrationDate(dateStr: string): string {
  return dateFormatter(dateStr, 'LL');
}

export function licenseExpirationDate(dateStr: ConfigType): string {
  return dateFormatter(dateStr, 'LL');
}

export function toShortIsoFormat(dateStr?: string) {
  return dayjs.utc(dateStr).format().split('Z')[0];
}

export function formatDayOfMonth(day?: string) {
  if (!day) {
    return '';
  }
  if (day === ALL_VALUES || day.split(',').length === 31) {
    return i18n.t('All days');
  }
  return day.split(',').join(', ');
}

export function formatWeekName(day_of_week, weekDays) {
  if (!day_of_week) {
    return '';
  }
  if (day_of_week === ALL_VALUES || day_of_week.split(',').length === 7) {
    return i18n.t('Every day');
  }
  return day_of_week.split(',').map((id: string) => weekDays.find((el) => el.id === Number(id) || el.key === id)?.name).join(', ');
}

export function formatMonthName(month, months) {
  if (!month) {
    return '';
  }
  if (month === ALL_VALUES || month.split(',').length === 12) {
    return i18n.t('Every month');
  }
  return month.split(',').map((id: string) => months.find((el) => el.id === Number(id) || el.key === id)?.name).join(', ');
}

export function formatLastRuntime(startedAt, endedAt) {
  if (startedAt && endedAt) {
    const maxDate = dayjs.max([dayjs(startedAt), dayjs(endedAt)]);
    return dateFormatter(maxDate);
  }
  if (startedAt) {
    return dateFormatter(startedAt);
  }
  return dateFormatter(endedAt);
}

export function formatCronTime(hour: string) {
  if (hour) {
    if (hour === ALL_VALUES) {
      return PRESET_ALL_VALUES.startTime.split(',');
    }
    const timeCombinations: string[] = [];
    const hourArr = hour.split(',').map(h => h.trim());

    hourArr.forEach((h: string) => {
      timeCombinations.push(`${h.padStart(2, '0')}:00`);
    });

    return uniq(timeCombinations);
  }
  return ['00:00'];
}

export function convertTimeArrayToHours(times: string[]): string {
  if (times) {
    return times.map(time => parseInt(time.split(':')[0], 10)).join(',');
  }
  return '';
}
